import { ReadonlyVec2, vec3, vec4 } from 'gl-matrix';
import { PlaneView } from '../../../view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';

import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { View } from '~/engine/view/View';
import { DrawObject } from '~/engine/tool/actions/DrawObject';
import {
    BooleanOperation,
    SDFOperations,
} from '~/engine/util/algorithms/sdf/SDFOperations';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { ModifierKeys } from '~/engine/tool/ModifierKeys';
import { ContourOperations } from '~/engine/util/algorithms/sdf/ContourOperations';

class Line {
    public pointsMm: vec3[];
    public hoverPointMm: vec3 | null;
    private readonly _epsilonToCloseLineMm = 0.5; // Epsilon to consider a point close enough to the first point to close the line
    private readonly _epsilonToAddPointMm = 0.1;
    private minLineLength = 10;

    constructor(startPoint: vec3) {
        this.pointsMm = [startPoint];
        this.hoverPointMm = null;
    }

    public hover(ptMm: vec3 | null) {
        this.hoverPointMm = ptMm;
    }

    public isOkToAdd(): boolean {
        return true;
    }

    public isClosingPoint(point: vec3): boolean {
        if (!this.hoverPointMm || this.pointsMm.length < 3) return false;

        const pointsClose = this.arePointsClose(
            point,
            this.pointsMm[0],
            this._epsilonToCloseLineMm
        );

        const lineLength = this.calculateTotalLength();
        const lengthSufficient = lineLength >= this.minLineLength;

        return pointsClose && lengthSufficient;
    }

    public addPoint(point: vec3): boolean {
        if (this.pointsMm.length > 0) {
            const lastPoint = this.pointsMm[this.pointsMm.length - 1];
            if (
                this.arePointsClose(point, lastPoint, this._epsilonToAddPointMm)
            ) {
                return false;
            }
        }
        const lineReady = this.isClosingPoint(point);
        this.pointsMm.push(point);
        this.hoverPointMm = null;
        return lineReady;
    }

    private arePointsClose(
        point1: vec3,
        point2: vec3,
        epsilonMm: number
    ): boolean {
        const dx = point1[0] - point2[0];
        const dy = point1[1] - point2[1];
        const dz = point1[2] - point2[2];
        const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
        return distance < epsilonMm;
    }

    private calculateTotalLength(): number {
        let length = 0;
        for (let i = 1; i < this.pointsMm.length; i++) {
            const p1 = this.pointsMm[i - 1];
            const p2 = this.pointsMm[i];
            const dx = p1[0] - p2[0];
            const dy = p1[1] - p2[1];
            length += Math.sqrt(dx * dx + dy * dy);
        }
        return length;
    }
}

export class LineDrawActionState {
    private line: Line | null = null;
    protected readonly _planeView: PlaneView;
    protected readonly margin: number;
    protected readonly resolution: ReadonlyVec2;
    protected readonly drawButton: number;
    protected readonly cancelButton: number;
    protected readonly drawObject: DrawObject;
    protected readonly target: RegionOfInterest;
    protected readonly targetImage: ImageObject;
    private readonly canvasContext: CanvasRenderingContext2D | null;
    private readonly isErasing: boolean;

    public constructor(
        view: PlaneView,
        target: RegionOfInterest,
        targetImage: ImageObject,
        margin: number,
        drawButton: number,
        cancelButton: number,
        isErasing: boolean
    ) {
        this.margin = margin;
        this._planeView = view;
        this.resolution = view.resolution;
        this.drawButton = drawButton;
        this.cancelButton = cancelButton;
        this.targetImage = targetImage;
        this.target = target;
        this.isErasing = isErasing;

        this.drawObject = new DrawObject(
            this.targetImage,
            this.resolution,
            view.getPatientSpaceBoundingBox(),
            vec4.fromValues(1, 0, 0, 1)
        );

        const canvas = this._planeView.get2DCanvas;
        if (canvas) {
            this.canvasContext = canvas.getContext('2d');
            if (this.canvasContext) {
                this.canvasContext.canvas.width = canvas.offsetWidth;
                this.canvasContext.canvas.height = canvas.offsetHeight;
                this.clearCanvas();
            } else {
                console.error('Canvas context is undefined.');
                this.canvasContext = null;
            }
        } else {
            console.error('Canvas is undefined.');
            this.canvasContext = null;
        }
    }

    public get drawing(): boolean {
        return this.line !== null;
    }

    public onMousePress(
        button: number,
        _modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        _view: View
    ): EventHandlerResult {
        if (button === this.drawButton) {
            const mousePoint =
                this._planeView.clientToPatientCoordinates(coordinates);

            if (!this.line) {
                this.line = new Line(mousePoint);
            } else {
                const lineReady = this.line.addPoint(mousePoint);
                if (lineReady) {
                    this.finishDrawing();
                    return new EventHandlerResult(
                        EventHandlerResultType.EVENT_HANDLED,
                        true,
                        new VolumeViewUpdateEvent(
                            this.target.boundingBox,
                            LayerBit.CONTOUR_LAYER_BIT,
                            true
                        )
                    );
                }
            }
            this.drawOnCanvas();
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        _view: View
    ): EventHandlerResult {
        if (this.line) {
            const mousePoint =
                this._planeView.clientToPatientCoordinates(coordinates);
            this.line.hover(mousePoint);

            if (buttons === 1 && this.line.isOkToAdd()) {
                const lineReady = this.line.addPoint(mousePoint);
                if (lineReady) {
                    this.finishDrawing();
                    return new EventHandlerResult(
                        EventHandlerResultType.EVENT_HANDLED,
                        true,
                        new VolumeViewUpdateEvent(
                            this.target.boundingBox,
                            LayerBit.CONTOUR_LAYER_BIT,
                            true
                        )
                    );
                }
            }
            this.drawOnCanvas();
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMouseRelease(
        button: number,
        _coordinates: ReadonlyVec2,
        _view?: View
    ): EventHandlerResult {
        if (button === this.cancelButton) {
            this.discardDrawing();
            this.stopAction();
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public getRoiColor() {
        const color = this.target.color;
        const R = Math.round(color[0] * 255);
        const G = Math.round(color[1] * 255);
        const B = Math.round(color[2] * 255);
        return { R, G, B };
    }

    public drawOnCanvas() {
        const canvas = this._planeView.get2DCanvas;
        if (!canvas || !this.canvasContext) {
            console.error('Canvas or context is undefined.');
            return;
        }

        const ctx = this.canvasContext;
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (this.line && this.line.pointsMm.length > 0) {
            const color = this.getRoiColor();
            ctx.strokeStyle = `rgba(${color.R}, ${color.G}, ${color.B}, 1)`;
            ctx.lineWidth =
                this.line.hoverPointMm !== null &&
                this.line.isClosingPoint(this.line.hoverPointMm)
                    ? 2
                    : 1;

            ctx.beginPath();
            const firstPt = this.line.pointsMm[0];
            const firstCanvasCoords =
                this._planeView.canvasCoordsFromPatientCoords(
                    vec3.fromValues(firstPt[0], firstPt[1], firstPt[2])
                );
            ctx.arc(
                firstCanvasCoords[0],
                firstCanvasCoords[1],
                3,
                0,
                2 * Math.PI
            );
            ctx.stroke();

            ctx.beginPath();
            ctx.moveTo(firstCanvasCoords[0], firstCanvasCoords[1]);

            for (let iPt = 1; iPt < this.line.pointsMm.length; ++iPt) {
                const point = this.line.pointsMm[iPt];
                const canvasCoords =
                    this._planeView.canvasCoordsFromPatientCoords(
                        vec3.fromValues(point[0], point[1], point[2])
                    );
                ctx.lineTo(canvasCoords[0], canvasCoords[1]);
            }

            if (this.line.hoverPointMm) {
                const hoverPt = this.line.hoverPointMm;
                const hoverCanvasCoords =
                    this._planeView.canvasCoordsFromPatientCoords(
                        vec3.fromValues(hoverPt[0], hoverPt[1], hoverPt[2])
                    );
                ctx.lineTo(hoverCanvasCoords[0], hoverCanvasCoords[1]);
            }

            ctx.stroke();
        }
    }

    public finishOnPoint(coordinates: ReadonlyVec2): EventHandlerResult {
        if (this.line) {
            const mousePoint =
                this._planeView.clientToPatientCoordinates(coordinates);
            this.line.addPoint(mousePoint);
            this.finishDrawing();
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_HANDLED,
                true,
                new VolumeViewUpdateEvent(
                    this.target.boundingBox,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    private finishDrawing() {
        this.performOperation(
            this._planeView,
            this.target,
            this.line,
            this.targetImage
        );
        this.discardDrawing();
    }

    protected get contourTexture(): ContourTexture | undefined {
        return this.drawObject.toContourTexture();
    }

    public clearMemory(): boolean {
        const canvas = this._planeView.get2DCanvas;
        if (!canvas) {
            console.error('Canvas is undefined.');
            return false;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            console.error('clearCanvas: Context is undefined or null.');
            return false;
        }
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        return true;
    }

    private clearCanvas(): void {
        if (this.canvasContext) {
            const { canvas } = this.canvasContext;
            this.canvasContext.clearRect(0, 0, canvas.width, canvas.height);
        }
    }

    private performOperation(
        view: PlaneView,
        roi: RegionOfInterest,
        line: Line | null,
        targetImage: ImageObject
    ) {
        if (!line) return;

        const polygonMm = line.pointsMm;
        const operation = new ContourOperations();
        const drawnSdf = operation.polygonToSdf(polygonMm, targetImage);

        const roiTexture = roi.texture;

        if (!roiTexture) {
            throw new Error('ROI texture is not available');
        }

        const booleanOperation = this.isErasing
            ? BooleanOperation.AND_NOT
            : BooleanOperation.OR;

        if (drawnSdf && this.target.texture) {
            const roiModify = roi.startModify(
                `Line ${this.isErasing ? 'erase' : 'draw'}`
            );

            SDFOperations.applyBoolean(
                roiTexture,
                drawnSdf,
                booleanOperation,
                this.target
            );
            drawnSdf.delete();
            roiModify.finish();
        } else {
            console.warn(`Attempt to apply boolean operation as a result of a draw action,
                but the drawing was undefined.`);
        }
    }

    public discardDrawing(): void {
        this.clearCanvas();
        this.line = null;
    }

    public stopAction(): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }
}
